import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Textarea } from '../ui/textarea';
import {
  Sparkles, Clock, Loader2, AlertCircle,
  FileText, BarChart3, TrendingUp, Mail, Send
} from 'lucide-react';

const WORK_TYPES = [
  { value: 'dev', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'support', label: 'Support' },
  { value: 'admin', label: 'Admin' },
  { value: 'learning', label: 'Learning' },
  { value: 'other', label: 'Other' },
];

export function TimesheetAISummary({
  aiSummary, aiLoading, reportText, sendingReport,
  onGenerateSummary, onSetReportText, onSendReport
}) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <Button onClick={() => onGenerateSummary('daily_report')} disabled={aiLoading} variant="outline" data-testid="ai-daily-report-btn">
          {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Generate Daily Report
        </Button>
        <Button onClick={() => onGenerateSummary('weekly_insights')} disabled={aiLoading} variant="outline" data-testid="ai-weekly-insights-btn">
          {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
          Weekly Insights
        </Button>
      </div>

      {aiSummary && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Clock} color="blue" label="Total Hours" value={aiSummary.stats?.total_hours?.toFixed(1) || 0} />
          <StatCard icon={TrendingUp} color="green" label="Billable %" value={`${((aiSummary.stats?.billable_ratio || 0) * 100).toFixed(0)}%`} />
          <StatCard icon={FileText} color="purple" label="Top Project" value={aiSummary.stats?.by_project?.[0]?.project_name || 'N/A'} small />
          <StatCard icon={BarChart3} color="orange" label="Top Work Type" value={WORK_TYPES.find(w => w.value === aiSummary.stats?.by_work_type?.[0]?.work_type)?.label || 'N/A'} small />
        </div>
      )}

      {aiSummary?.insights?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Working Pattern Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {aiSummary.insights.map((insight, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-sm">{insight}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {aiSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Daily Report for Managers
            </CardTitle>
            <CardDescription>
              AI draft is editable. The final text you send is treated as your own report.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={reportText}
              onChange={(e) => onSetReportText(e.target.value)}
              placeholder="Your daily report..."
              className="min-h-[200px] font-mono text-sm"
              data-testid="ai-report-textarea"
            />
            <div className="flex justify-end">
              <Button onClick={onSendReport} disabled={sendingReport} data-testid="ai-send-report-btn">
                {sendingReport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send to Managers
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, small }) {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorMap[color]?.split(' ')[0]}`}>
            <Icon className={`h-5 w-5 ${colorMap[color]?.split(' ')[1]}`} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={small ? 'text-lg font-semibold truncate' : 'text-2xl font-bold'}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
